import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import type { AuthContextType, AuthState, User, LoginCredentials, ChangePasswordCredentials } from '@/shared/types/auth'
import { loginApi, getCurrentUserApi, changePasswordApi, refreshTokenApi, logoutApi } from '../api/auth.api'
import { queryClient } from '@/config/query-client'

export const AuthContext = createContext<AuthContextType | undefined>(undefined)

const PROFIL_TO_ROLE: Record<string, string> = {
  ETUDIANT: 'etudiant',
  FORMATEUR: 'professeur',
  ADMIN_WEEHIVE: 'admin',
  PARENT: 'parent',
}

function mapProfilToRole(userData: Record<string, unknown>): User {
  return {
    ...userData,
    role: PROFIL_TO_ROLE[userData.profil as string] || (userData.role as string) || 'etudiant',
  } as User
}

function getRefreshToken(): string | null {
  return localStorage.getItem('refreshToken') || sessionStorage.getItem('refreshToken')
}

function storeRefreshToken(token: string, persistent: boolean) {
  if (persistent) {
    localStorage.setItem('refreshToken', token)
    sessionStorage.removeItem('refreshToken')
  } else {
    sessionStorage.setItem('refreshToken', token)
    localStorage.removeItem('refreshToken')
  }
}

function clearRefreshToken() {
  localStorage.removeItem('refreshToken')
  sessionStorage.removeItem('refreshToken')
}

function isRefreshTokenPersistent(): boolean {
  return !!localStorage.getItem('refreshToken')
}

export function useAuthProvider(): AuthContextType {
  const hasToken = typeof window !== 'undefined' && !!(localStorage.getItem('token') || getRefreshToken())

  const [state, setState] = useState<AuthState>({
    isAuthenticated: false,
    user: null,
    loading: hasToken,
    error: null,
  })

  const fetchUserProfile = useCallback(async () => {
    try {
      const token = localStorage.getItem('token')
      if (!token) {
        setState(prev => ({ ...prev, isAuthenticated: false, user: null, loading: false, error: null }))
        return
      }

      const userData = await getCurrentUserApi()
      const userWithCorrectRole = mapProfilToRole(userData as unknown as Record<string, unknown>)

      setState(prev => ({
        ...prev,
        user: userWithCorrectRole,
        isAuthenticated: true,
        loading: false,
        error: null,
      }))
    } catch (error: unknown) {
      const axiosError = error as { response?: { status: number } }

      const status = axiosError.response?.status
      if (status === 401 || status === 403) {
        localStorage.removeItem('token')
        setState(prev => ({ ...prev, isAuthenticated: false, user: null, loading: false, error: null }))
        return
      }

      setState(prev => ({ ...prev, isAuthenticated: false, user: null, loading: false, error: null }))
    }
  }, [])

  const tryRefreshToken = useCallback(async (): Promise<boolean> => {
    const rt = getRefreshToken()
    if (!rt) return false

    try {
      const persistent = isRefreshTokenPersistent()
      const data = await refreshTokenApi(rt)
      if (data.result === 'SUCCESS' && data.data) {
        localStorage.setItem('token', data.data.token)
        storeRefreshToken(data.data.refreshToken, persistent)
        return true
      }
    } catch {
      clearRefreshToken()
      localStorage.removeItem('token')
    }
    return false
  }, [])

  useEffect(() => {
    const init = async () => {
      const token = localStorage.getItem('token')
      if (token) {
        await fetchUserProfile()
      } else {
        // Pas d'access token mais peut-être un refresh token
        const refreshed = await tryRefreshToken()
        if (refreshed) {
          await fetchUserProfile()
        } else {
          setState(prev => ({ ...prev, loading: false, isAuthenticated: false }))
        }
      }
    }
    init()
  }, [fetchUserProfile, tryRefreshToken])

  const login = useCallback(async (credentials: LoginCredentials & { rememberMe?: boolean }) => {
    setState(prev => ({ ...prev, loading: true, error: null }))

    try {
      const data = await loginApi(credentials)

      if (data.result === 'SUCCESS' && data.data) {
        const { token, refreshToken, user } = data.data
        localStorage.setItem('token', token)

        // Sur les pages mobiles, toujours persister. Sinon, selon le choix "Se souvenir de moi"
        const isMobile = window.location.pathname.startsWith('/mobile')
        const persistent = isMobile || credentials.rememberMe !== false
        storeRefreshToken(refreshToken, persistent)

        if (user) {
          const userWithCorrectRole = mapProfilToRole(user as unknown as Record<string, unknown>)
          setState(prev => ({ ...prev, user: userWithCorrectRole, isAuthenticated: true, loading: false, error: null }))
        } else {
          await fetchUserProfile()
        }
      } else {
        throw new Error('Réponse invalide du serveur')
      }
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { message?: string } }; message?: string }
      const errorMessage = axiosError.response?.data?.message || 'Email ou mot de passe incorrect'
      setState(prev => ({ ...prev, loading: false, error: errorMessage }))
      throw error
    }
  }, [fetchUserProfile])

  const changePassword = useCallback(async (credentials: ChangePasswordCredentials) => {
    setState(prev => ({ ...prev, loading: true, error: null }))

    try {
      const data = await changePasswordApi(credentials)

      if (data.result === 'SUCCESS' && data.data) {
        const { token, user } = data.data
        localStorage.setItem('token', token)

        if (user) {
          const userWithCorrectRole = mapProfilToRole(user as unknown as Record<string, unknown>)
          setState(prev => ({ ...prev, user: userWithCorrectRole, isAuthenticated: true, loading: false, error: null }))
        } else {
          await fetchUserProfile()
        }
      } else {
        throw new Error('Échec du changement de mot de passe')
      }
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { message?: string } }; message?: string }
      const errorMessage = axiosError.response?.data?.message || 'Erreur lors du changement de mot de passe'
      setState(prev => ({ ...prev, loading: false, error: errorMessage }))
      throw error
    }
  }, [fetchUserProfile])

  const logout = useCallback(async () => {
    const rt = getRefreshToken()
    if (rt) {
      try { await logoutApi(rt) } catch { /* ignore */ }
    }
    localStorage.removeItem('token')
    clearRefreshToken()
    queryClient.clear()
    setState({ isAuthenticated: false, user: null, loading: false, error: null })
  }, [])

  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }))
  }, [])

  return { ...state, login, logout, changePassword, clearError }
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
